import type { ComponentProps, Key, ReactNode } from 'react'
import { Button } from './button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'

export interface Column<T> {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  className?: string
}
export interface TablePagination {
  page: number
  size: number
  total: number
  onChange: (page: number, size: number) => void
}
export function Pagination({ page, size, total, onChange }: TablePagination) {
  const pages = Math.max(1, Math.ceil(total / size))
  return (
    <nav
      aria-label="分頁"
      className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t pt-4 text-sm"
    >
      <span className="text-xs text-muted-foreground">
        共 {total.toLocaleString('en-US')} 筆 · 第 {page} / {pages} 頁
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          每頁
          <select
            aria-label="每頁筆數"
            className="h-8 rounded border border-input bg-white px-2 text-foreground"
            value={size}
            onChange={(event) => onChange(1, Number(event.target.value))}
          >
            {[...new Set([10, 20, 50, 100, size])]
              .sort((a, b) => a - b)
              .map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
          </select>
          筆
        </label>
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1, size)}
        >
          上一頁
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => onChange(page + 1, size)}
        >
          下一頁
        </Button>
      </div>
    </nav>
  )
}
export function DataTable<T>({
  columns,
  data,
  rowKey,
  loading,
  emptyText = '暫無資料',
  pagination,
  onRow,
}: {
  columns: Column<T>[]
  data: T[]
  rowKey: keyof T | ((row: T) => Key)
  loading?: boolean
  emptyText?: string
  pagination?: TablePagination
  onRow?: (row: T) => ComponentProps<'tr'>
}) {
  return (
    <div className="min-w-0">
      <Table aria-busy={loading}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                scope="col"
                className={column.className}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="py-12 text-center text-muted-foreground"
                role="status"
              >
                載入中...
              </TableCell>
            </TableRow>
          ) : !data.length ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="py-12 text-center text-muted-foreground"
              >
                {emptyText}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => (
              <TableRow
                key={
                  typeof rowKey === 'function'
                    ? rowKey(row)
                    : String(row[rowKey])
                }
                {...onRow?.(row)}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} className={column.className}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div aria-live="polite">
        {pagination && <Pagination {...pagination} />}
      </div>
    </div>
  )
}
